import { useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useDiffitUserContext } from '../contexts/DiffitUserContext';
import {
  ASK_ADMIN,
  resolveCanAskColleaguesToShare,
  usePremiumOffer,
} from '../contexts/premiumOffer';
import { isInExpiryWindow, resolveExpiringSoonWindowDays } from '../utils/churnedOrg';
import { getResolvedGeo } from '../services/GeoService';
import { resolvePlanTier } from '../utils/planTier';
import { isReferralProgramKeyConfigured } from '../utils/referralProgramConfig';
import { MATH_REFERRAL_PROGRAM_KEY } from '../constants/mathPromo';
import { SEPTEMBER_SCRAMBLE_PROGRAM_KEY } from '../constants/septemberScramble';
import { getCurricula } from '../services/CurriculumService';
import { loadReferralAdCopy } from './referralAdCopy';
import { pickAnnouncedCurriculum } from './curriculumAnnouncement';

/**
 * Everything an ad rule can target on, derived once for every surface.
 *
 * The terms here are all user- or campaign-level, so they answer the same
 * question wherever the teacher is standing. Anything that depends on WHERE the
 * slot is — the resource in view, the route, the export that just finished —
 * stays with the surface and is merged into the selector ctx there.
 *
 * `inAppAdsCopy` is the one non-scalar here, and it is not a targeting term at
 * all — it never reaches the selector, only the copy of the ad that already won.
 * LaunchDarkly holds one object per flag value, so its identity is stable
 * between flag changes and it is safe in a dependency array.
 *
 * Returns scalars, not a ctx object: callers put these straight into a selection
 * effect's dependency array, and an object literal rebuilt every render would
 * re-run selection every render.
 */
export function useAdContext() {
  const {
    diffitUser,
    setDiffitUser,
    hasActiveSubscription,
    isFreeTrialUser,
    isLDidentified,
    isSignedIn,
    isRealDiffitAdmin,
  } = useDiffitUserContext();
  const {
    inAppAdsCopy,
    referralProgramConfiguration,
    sidebarChromeExtensionCard,
    expiringSoonWindowDays,
    extraCurricula,
    alignToCurriculum,
  } = useFlags();
  const windowDays = resolveExpiringSoonWindowDays(expiringSoonWindowDays);
  const {
    isOffered,
    ask,
    isSettled: isPremiumOfferSettled,
    canShareWithAdmin: isAdminAskUsable,
    offersDays: offersExtensionDays,
  } = usePremiumOffer();

  const tier = resolvePlanTier({ isLDidentified, hasActiveSubscription, isFreeTrialUser });
  const countryCode = getResolvedGeo()?.country_code;
  const seenNux = diffitUser?.seen_nux_json;
  const hasClaimedMathOffer = !!seenNux?.math_promo_claimed;

  // Only the two expiry campaigns read the offer, and their local half — a licence inside the
  // window, or a school that recently lapsed — is answerable with no network at all. Everyone
  // else's slot resolves immediately rather than sitting behind a round trip that cannot
  // change which ad they get.
  const couldExpiryAdWin = isInExpiryWindow(diffitUser, windowDays);

  const isReferralCampaignLive = isReferralProgramKeyConfigured(
    referralProgramConfiguration,
    SEPTEMBER_SCRAMBLE_PROGRAM_KEY
  );

  const isMathShareCampaignLive = isReferralProgramKeyConfigured(
    referralProgramConfiguration,
    MATH_REFERRAL_PROGRAM_KEY
  );

  // undefined while loading (or not applicable); an object once settled — `{}` on failure.
  const [referralAdCopy, setReferralAdCopy] = useState(undefined);
  const needsReferralAdCopy = isReferralCampaignLive && isSignedIn;
  useEffect(() => {
    if (!needsReferralAdCopy) return undefined;
    let cancelled = false;
    loadReferralAdCopy().then((copy) => {
      if (!cancelled) setReferralAdCopy(copy);
    });
    return () => {
      cancelled = true;
    };
  }, [needsReferralAdCopy]);

  // The published index tells a math curriculum from the rest. Fetched only for a teacher whose
  // district has turned one on; `undefined` while loading, `[]` on failure.
  const wantsCurriculumIndex =
    isSignedIn && !!alignToCurriculum && Array.isArray(extraCurricula) && extraCurricula.length > 0;
  const [curriculumIndex, setCurriculumIndex] = useState(undefined);
  useEffect(() => {
    if (!wantsCurriculumIndex) return undefined;
    let cancelled = false;
    getCurricula()
      .then((entries) => !cancelled && setCurriculumIndex(entries))
      .catch(() => !cancelled && setCurriculumIndex([]));
    return () => {
      cancelled = true;
    };
  }, [wantsCurriculumIndex]);
  const announcedCurriculum = wantsCurriculumIndex
    ? pickAnnouncedCurriculum(curriculumIndex, extraCurricula)
    : null;

  return {
    diffitUser,
    setDiffitUser,
    isSignedIn,
    isLDidentified,
    isRealDiffitAdmin,
    inAppAdsCopy,
    referralAdCopy: needsReferralAdCopy ? referralAdCopy : undefined,
    expiringSoonWindowDays: windowDays,
    seenNux,
    tier,
    countryCode,
    hasClaimedMathOffer,
    isReferralCampaignLive,
    isMathShareCampaignLive,
    // Audience is set in LaunchDarkly (one `email` ends-with rule per district), not here.
    isChromeExtensionAudience: !!sidebarChromeExtensionCard,
    isPremiumOfferPending: couldExpiryAdWin && !isPremiumOfferSettled,
    // Whether "ask my admin to renew" is a route that actually works for this teacher.
    // POST /user/share-with-admin is hard-gated on the paygate-premium-offer flag and on a
    // school sender domain, and the offer resolves to the admin ask only when both hold — so
    // an ad whose only CTA opens that modal has to target this, not "is not premium".
    canAskAdminToRenew: isOffered && ask === ASK_ADMIN,
    // Whether the renewal ask merely WORKS, regardless of which route the school earned. The
    // licence-expiring card targets this rather than canAskAdminToRenew: its invite twin can
    // never exist — the referral reward is the same 30 days, and a teacher still inside their
    // licence cannot be granted them — so a small school gets the admin ask or nothing.
    canShareWithAdmin: isOffered && isAdminAskUsable,
    // The other half of the same question. A school too small to have anyone holding a budget
    // earns the invite ask instead, which pays the 30 days through the referral program — so
    // the expiry cards have a version for them rather than staying silent.
    canAskColleaguesToShare: resolveCanAskColleaguesToShare(isOffered, ask),
    // Whether the 30-day thank-you will actually be granted. Copy that promises it targets
    // this, so a teacher who has already spent the code is never promised it twice.
    offersExtensionDays,
    // The math curriculum this teacher's district turned on (LD `extra-curricula`), if any.
    announcedCurriculumId: announcedCurriculum?.id ?? null,
    announcedCurriculumName: announcedCurriculum?.name ?? null,
    announcedCurriculumGrades: announcedCurriculum?.grades ?? null,
    // Waited on like the premium offer, so a lower card never paints and then gets replaced.
    isCurriculumIndexPending: wantsCurriculumIndex && curriculumIndex === undefined,
  };
}
